import { useState, useEffect } from 'react'
import { X, Check } from 'lucide-react'
import { Campaign, CampaignType, TimingConfig, Template } from '@/types'
import { useMailboxes } from '@hooks/useMailboxes'
import { useTemplates } from '@hooks/useTemplates'
import { useUpdateCampaign } from '@hooks/useCampaigns'
import { toast } from 'react-hot-toast'

interface EditCampaignDialogProps {
  isOpen: boolean
  onClose: () => void
  campaign: Campaign
  onSuccess?: () => void
}

const CAMPAIGN_TYPES = [
  { type: 'WELCOME_SERIES' as CampaignType, name: 'Welcome Series', emails: 3, icon: '👋' },
  { type: 'RE_ENGAGEMENT' as CampaignType, name: 'Re-engagement', emails: 4, icon: '🔄' },
  { type: 'NEWSLETTER' as CampaignType, name: 'Newsletter', emails: 1, icon: '📧' },
  { type: 'PRODUCT_LAUNCH' as CampaignType, name: 'Product Launch', emails: 5, icon: '🚀' },
  { type: 'EVENT_PROMOTION' as CampaignType, name: 'Event Promotion', emails: 3, icon: '🎪' },
  { type: 'CUSTOM' as CampaignType, name: 'Custom Campaign', emails: 0, icon: '⚙️' },
]

export function EditCampaignDialog({ isOpen, onClose, campaign, onSuccess }: EditCampaignDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    mailboxId: null as number | null,
    campaignType: null as CampaignType | null,
    template1Id: null as number | null,
    template2Id: null as number | null,
    template3Id: null as number | null,
    template4Id: null as number | null,
    template5Id: null as number | null,
    timingConfig: {} as TimingConfig,
  })

  const { data: mailboxesData } = useMailboxes()
  const mailboxes = mailboxesData?.data || []
  const { data: templatesData } = useTemplates()
  const templates = templatesData?.data || []
  const updateCampaign = useUpdateCampaign(campaign.id)

  // Initialize form data from campaign prop
  useEffect(() => {
    if (campaign && isOpen) {
      setFormData({
        name: campaign.name || '',
        mailboxId: campaign.mailboxId || null,
        campaignType: campaign.campaignType || null,
        template1Id: campaign.template1Id || null,
        template2Id: campaign.template2Id || null,
        template3Id: campaign.template3Id || null,
        template4Id: campaign.template4Id || null,
        template5Id: campaign.template5Id || null,
        timingConfig: campaign.timingConfig || {},
      })
    }
  }, [campaign, isOpen])

  const selectedCampaignType = CAMPAIGN_TYPES.find((ct) => ct.type === formData.campaignType)
  const emailCount = selectedCampaignType?.emails || 0

  const handleClose = () => {
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error('Please enter a campaign name')
      return
    }

    if (!formData.mailboxId) {
      toast.error('Please select a mailbox')
      return
    }

    try {
      await updateCampaign.mutateAsync({
        name: formData.name,
        mailboxId: formData.mailboxId,
        campaignType: formData.campaignType ?? undefined,
        template1Id: formData.template1Id,
        template2Id: formData.template2Id,
        template3Id: formData.template3Id,
        template4Id: formData.template4Id,
        template5Id: formData.template5Id,
        timingConfig: Object.keys(formData.timingConfig).length > 0 ? formData.timingConfig : undefined,
      })

      toast.success('Campaign updated successfully')
      handleClose()
      onSuccess?.()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update campaign')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-700">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-100">Edit Campaign</h2>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-slate-200 transition-colors p-2 rounded-lg hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            {/* Campaign Name */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Campaign Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter campaign name..."
                className="input-modern"
              />
            </div>

            {/* Mailbox Selection */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-300">Sending Mailbox</label>
              <select
                value={formData.mailboxId || ''}
                onChange={(e) =>
                  setFormData({ ...formData, mailboxId: e.target.value ? Number(e.target.value) : null })
                }
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              >
                <option value="">Select a mailbox...</option>
                {mailboxes
                  ?.filter((mb) => mb.isActive)
                  .map((mailbox) => (
                    <option key={mailbox.id} value={mailbox.id}>
                      {mailbox.name} ({mailbox.email})
                    </option>
                  ))}
              </select>
            </div>

            {/* Campaign Type (read-only display) */}
            {formData.campaignType && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300">Campaign Type</label>
                <div className="flex items-center gap-2 p-3 bg-slate-800/50 rounded-xl border border-slate-700">
                  <span className="text-2xl">{selectedCampaignType?.icon}</span>
                  <span className="font-medium text-slate-100">{selectedCampaignType?.name}</span>
                  <span className="text-sm text-slate-400 ml-auto">
                    {emailCount > 0 ? `${emailCount} email${emailCount > 1 ? 's' : ''}` : 'Custom'}
                  </span>
                </div>
              </div>
            )}

            {/* Template Selection */}
            {emailCount > 0 && (
              <div className="space-y-4 border-t border-slate-700 pt-6">
                <h4 className="text-md font-medium text-slate-100">Email Templates</h4>
                {Array.from({ length: emailCount }, (_, i) => i + 1).map((emailNum) => (
                  <div key={emailNum} className="space-y-2">
                    <label className="block text-sm font-medium text-slate-300">
                      Email {emailNum} Template
                    </label>
                    <select
                      value={
                        emailNum === 1
                          ? formData.template1Id || ''
                          : emailNum === 2
                          ? formData.template2Id || ''
                          : emailNum === 3
                          ? formData.template3Id || ''
                          : emailNum === 4
                          ? formData.template4Id || ''
                          : formData.template5Id || ''
                      }
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : null
                        setFormData({
                          ...formData,
                          ...(emailNum === 1 && { template1Id: value }),
                          ...(emailNum === 2 && { template2Id: value }),
                          ...(emailNum === 3 && { template3Id: value }),
                          ...(emailNum === 4 && { template4Id: value }),
                          ...(emailNum === 5 && { template5Id: value }),
                        })
                      }}
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-xl text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    >
                      <option value="">Select a template...</option>
                      {templates.map((template: Template) => (
                        <option key={template.id} value={template.id}>
                          {template.name} - {template.subject}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            {/* Timing Configuration */}
            {emailCount > 0 && (
              <div className="space-y-4 border-t border-slate-700 pt-6">
                <h4 className="text-md font-medium text-slate-100">Email Timing</h4>
                <p className="text-sm text-slate-400">Configure when each email should be sent (days from enrollment)</p>

                <div className="space-y-3">
                  {Array.from({ length: emailCount }, (_, i) => i + 1).map((emailNum) => (
                    <div key={emailNum} className="flex items-center space-x-4">
                      <label className="text-sm font-medium text-slate-300 w-24">Email {emailNum}:</label>
                      <div className="flex items-center space-x-2 flex-1">
                        <span className="text-sm text-slate-400">Day</span>
                        <input
                          type="number"
                          min="0"
                          max="365"
                          value={
                            emailNum === 1
                              ? formData.timingConfig.day1 ?? 0
                              : emailNum === 2
                              ? formData.timingConfig.day2 ?? 3
                              : emailNum === 3
                              ? formData.timingConfig.day3 ?? 7
                              : emailNum === 4
                              ? formData.timingConfig.day4 ?? 14
                              : formData.timingConfig.day5 ?? 21
                          }
                          onChange={(e) => {
                            const value = parseInt(e.target.value)
                            setFormData({
                              ...formData,
                              timingConfig: {
                                ...formData.timingConfig,
                                ...(emailNum === 1 && { day1: value }),
                                ...(emailNum === 2 && { day2: value }),
                                ...(emailNum === 3 && { day3: value }),
                                ...(emailNum === 4 && { day4: value }),
                                ...(emailNum === 5 && { day5: value }),
                              },
                            })
                          }}
                          className="w-20 px-2 py-1 bg-slate-800 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                      </div>
                    </div>
                  ))}

                  <div className="flex items-center space-x-4 pt-2">
                    <label className="text-sm font-medium text-slate-300 w-24">Send Time:</label>
                    <input
                      type="time"
                      value={formData.timingConfig.sendTime || '09:00'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          timingConfig: { ...formData.timingConfig, sendTime: e.target.value },
                        })
                      }
                      className="px-2 py-1 bg-slate-800 border border-slate-600 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-end space-x-3 bg-slate-800/50">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-slate-400 hover:text-slate-200 transition-colors"
            disabled={updateCampaign.isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={updateCampaign.isPending}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4" />
            <span>{updateCampaign.isPending ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
